// src/components/admin/AssignTemplateModal.js
import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select, MenuItem, CircularProgress, Box, Alert } from '@mui/material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

export default function AssignTemplateModal({ open, onClose, onSave, currentUserName }) {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            const fetchTemplates = async () => {
                setLoading(true);
                setError(null);
                try {
                    const templatesRef = collection(db, 'questionnaireTemplates');
                    const q = query(templatesRef, where('status', '==', 'Active'), orderBy('name'));
                    const querySnapshot = await getDocs(q);
                    const activeTemplates = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setTemplates(activeTemplates);
                } catch (err) {
                    console.error("Error fetching active templates:", err);
                    setError("Failed to load questionnaire templates. Please try again.");
                } finally {
                    setLoading(false);
                }
            };
            fetchTemplates();
        }
    }, [open]);

    const handleSave = async () => {
        if (!selectedTemplate) return;
        const templateToSave = templates.find(t => t.id === selectedTemplate);
        setIsSaving(true);
        // The onSave function is expected to be async and return a promise
        try {
            await onSave(templateToSave);
        } catch (e) {
            // Error is handled by the parent component's toast message
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Assign Questionnaire Template to {currentUserName}</DialogTitle>
            <DialogContent>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {!loading && !error && (
                    <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
                        <InputLabel id="template-select-label">Questionnaire Template</InputLabel>
                        <Select
                            labelId="template-select-label"
                            value={selectedTemplate}
                            label="Questionnaire Template"
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                        >
                            {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                    {template.name} (v{template.version})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={!selectedTemplate || isSaving}>
                    {isSaving ? <CircularProgress size={24} /> : "Save Assignment"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}